import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ArrowRight } from 'lucide-react';
import Img from '@/components/shared/Img';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services' },
  { label: 'Our Work', path: '/gallery' },
  { label: 'About', path: '/about' },
  { label: 'Contact', path: '/contact' },
];

const serviceLinks = [
  { label: 'Kitchen Remodeling', path: '/services/kitchen-remodeling' },
  { label: 'Bathroom Remodeling', path: '/services/bathroom-remodeling' },
  { label: 'Home Additions', path: '/services/home-additions' },
  { label: 'Custom Builds', path: '/services/custom-builds' },
  { label: 'Interior Finishes', path: '/services/interior-finishes' },
  { label: 'Flooring', path: '/services/flooring' },
  { label: 'Painting', path: '/services/painting' },
  { label: 'Roofing', path: '/services/roofing' },
  { label: 'Home Systems & Upgrades', path: '/services/home-systems-upgrades' },
  { label: 'EV Charger Installation', path: '/services/ev-charger-installation' },
  { label: 'ADA Compliance', path: '/services/ada-compliance' },
  { label: 'Light Commercial', path: '/services/commercial-remodeling' },
];

export default function Footer() {
  // The mobile call bar is fixed 72px up from the bottom of the viewport. Without
  // reserved space the last footer row — copyright and the Privacy / Terms /
  // Accessibility links — sits permanently underneath it and can never be
  // scrolled clear. The padding goes on the <footer> rather than <body> so the
  // reserved strip keeps the footer's own dark colour instead of showing a band
  // of page background. The bar clears at 72px; the rest is breathing room, and
  // it is dropped at md where the bar is hidden.
  return (
    <footer className="bg-[#0a0a0a] text-white/70 border-t border-white/10 pb-[calc(5.75rem+env(safe-area-inset-bottom))] md:pb-0">
      <div>
        {/* Phone CTA Banner */}
        <div className="bg-[#0a0a0a] border-t border-white/10" style={{ borderLeft: '4px solid #C97B2E' }}>
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h2 className="font-heading text-white text-xl font-bold">Ready to get started?</h2>
              <p className="font-body text-white/50 text-sm">Free estimates — no pressure, just an honest conversation.</p>
            </div>
            <a
              href="tel:2812037688"
              className="flex items-center gap-2 bg-[#C97B2E] text-[#0a0a0a] px-6 py-3 rounded-sm font-body font-bold uppercase tracking-wider text-sm hover:brightness-110 transition-colors whitespace-nowrap"
            >
              <Phone className="w-4 h-4" aria-hidden="true" />
              Call Justin: (281) 203-7688
            </a>
          </div>
        </div>

        {/* Main Footer */}
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand */}
            <div className="lg:col-span-1">
              <div className="mb-4">
                <Img
                  src="/images/33e6a8c4c_logowhitetext.jpg"
                  alt="Terza Costa Construction — Rockport TX Remodeling Contractor"
                  className="h-14 w-auto object-contain"
                  sizes="240px"
                />
              </div>
              <p className="text-sm leading-relaxed mt-4 text-white/60 max-w-xs">
                Licensed & insured remodeling and construction — locally owned and operated in Rockport, Texas, serving the entire Coastal Bend.
              </p>
              <p className="font-heading text-[#C97B2E] text-sm italic mt-4">
                "Quality Work Starts with Quality Preparation."
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 mt-6 bg-[#C97B2E] text-[#0a0a0a] px-5 py-3 rounded-sm font-body font-bold uppercase tracking-wider text-xs hover:brightness-110 transition-colors duration-300 group"
              >
                Free Estimate
                <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </Link>
            </div>

            {/* Quick Links */}
            <div>
              <h2 className="font-heading text-white text-base mb-5">Quick Links</h2>
              <div className="flex flex-col gap-2.5">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="text-sm font-body text-white/60 hover:text-[#C97B2E] transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Services */}
            <div>
              <h2 className="font-heading text-white text-base mb-5">Services</h2>
              <div className="flex flex-col gap-2.5">
                {serviceLinks.map((s) => (
                  <Link
                    key={s.path}
                    to={s.path}
                    className="text-sm font-body text-white/60 hover:text-[#C97B2E] transition-colors duration-300"
                  >
                    {s.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div>
              <h2 className="font-heading text-white text-base mb-5">Contact</h2>
              <div className="flex flex-col gap-4">
                <a
                  href="tel:2812037688"
                  className="flex items-center gap-3 text-sm text-white/60 hover:text-[#C97B2E] transition-colors"
                >
                  <Phone className="w-4 h-4 text-[#C97B2E] flex-shrink-0" />
                  (281) 203-7688
                </a>
                <a
                  href="mailto:justin@tercosta.com"
                  className="flex items-center gap-3 text-sm text-white/60 hover:text-[#C97B2E] transition-colors"
                >
                  <Mail className="w-4 h-4 text-[#C97B2E] flex-shrink-0" />
                  justin@tercosta.com
                </a>
                <a
                  href="https://www.facebook.com/profile.php?id=61586631481744"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-sm text-white/60 hover:text-[#C97B2E] transition-colors"
                >
                  <svg className="w-4 h-4 text-[#C97B2E] flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Facebook
                </a>
                <div className="flex items-start gap-3 text-sm text-white/60">
                  <MapPin className="w-4 h-4 text-[#C97B2E] flex-shrink-0 mt-0.5" />
                  <div>
                    <p>Rockport, Texas</p>
                    <p className="text-white/60 text-xs mt-0.5 mb-2">Serving the entire Coastal Bend</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                      {[
                        { label: 'Rockport', path: '/locations/rockport' },
                        { label: 'Corpus Christi', path: '/locations/corpus-christi' },
                        { label: 'Aransas Pass', path: '/locations/aransas-pass' },
                        { label: 'Portland', path: '/locations/portland' },
                      ].map((c) => (
                        <Link
                          key={c.path}
                          to={c.path}
                          className="text-xs font-body text-white/60 hover:text-[#C97B2E] transition-colors duration-300"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-white/60 font-body">
              © {new Date().getFullYear()} Terza Costa Construction. All rights reserved. Licensed & Insured — Rockport, TX.{" "}
              Site by <a href="https://zonkelmedia.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#C97B2E] transition-colors underline underline-offset-2">Zonkel Media</a>
            </p>
            <nav aria-label="Legal" className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
              <Link to="/privacy-policy" className="text-xs text-white/60 font-body hover:text-[#C97B2E] transition-colors">Privacy Policy</Link>
              <span className="text-white/60 text-xs" aria-hidden="true">·</span>
              <Link to="/terms-of-service" className="text-xs text-white/60 font-body hover:text-[#C97B2E] transition-colors">Terms of Service</Link>
              <span className="text-white/60 text-xs" aria-hidden="true">·</span>
              <Link to="/accessibility" className="text-xs text-white/60 font-body hover:text-[#C97B2E] transition-colors">Accessibility</Link>
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
}